<template>
	<div>
		<v-main>
			<v-container fluid class="px-4">
				<div class="mb-2">
					<h2 class="text-h6 font-weight-medium">Analytics</h2>
					<div class="text-caption grey--text">Visualizzazioni di ogni pagina del sito negli ultimi 12 mesi chiusi, da Google Analytics. I dati del mese si aggiornano il 1° del mese successivo.</div>
				</div>

				<v-alert v-if="nonConfigurato" type="info" outlined dense class="mb-3">
					Google Analytics non è collegato a questo negozio: attiva il servizio Google Analytics nelle impostazioni dei servizi (ID proprietà e credenziali) per vedere le visite delle pagine.
				</v-alert>
				<v-alert v-else-if="error" type="error" dense outlined class="mb-3">{{ error }}</v-alert>

				<v-alert v-if="status && status.syncing" type="info" dense outlined class="mb-3">
					Scansione dei mesi da Google Analytics in corso ({{ status.ready }}/{{ status.total }} mesi pronti): la pagina si aggiorna da sola.
					<v-progress-linear class="mt-2" color="primary" :value="(status.ready / status.total) * 100" />
				</v-alert>
				<v-alert v-else-if="status && status.error && status.ready < status.total" type="warning" dense outlined class="mb-3">
					La scansione da Google Analytics non è riuscita ({{ status.ready }}/{{ status.total }} mesi pronti): controlla le credenziali del servizio. Verrà ritentata tra qualche minuto.
				</v-alert>

				<template v-if="data">
					<v-tabs v-model="tab" show-arrows class="mb-1">
						<v-tab v-for="l in data.languages" :key="l.lang">{{ nomeLingua(l.lang) }}</v-tab>
					</v-tabs>
					<div v-if="linguaAttiva" class="mb-3">
						<a :href="linguaAttiva.url" target="_blank" rel="noopener">{{ linguaAttiva.url }}</a>
					</div>

					<div class="d-flex align-center flex-wrap mb-2" style="gap: 12px">
						<v-text-field v-model="searchfield" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify" label="Cerca pagina" style="max-width: 320px" @input="cercaDopo" />
						<v-btn-toggle :value="sort" mandatory dense color="primary" @change="cambiaOrdine">
							<v-btn small value="asc"><v-icon small left>mdi-sort-ascending</v-icon>Meno viste</v-btn>
							<v-btn small value="desc"><v-icon small left>mdi-sort-descending</v-icon>Più viste</v-btn>
						</v-btn-toggle>
						<span class="text-caption grey--text">ordinate per {{ ultimoMese }}</span>
						<v-spacer />
						<span class="text-caption grey--text">{{ data.total.toLocaleString('it-IT') }} pagine</span>
					</div>

					<v-card outlined :loading="loading">
						<v-card-text class="py-0">
							<PageRow v-for="row in data.rows" :key="row.path" :row="row" :months="data.months" :scanned="status && status.months" @open="apriDettaglio" />
							<div v-if="!data.rows.length && !loading" class="py-6 text-center grey--text">
								{{ status && status.syncing ? 'I dati arrivano a scansione conclusa.' : 'Nessuna pagina con visite per questa lingua.' }}
							</div>
						</v-card-text>
					</v-card>

					<v-pagination v-if="pagine > 1" v-model="settings.options.page" class="mt-3" :length="pagine" :total-visible="9" />
				</template>
				<v-skeleton-loader v-else-if="loading" type="card-heading, list-item-three-line, list-item-three-line" />
			</v-container>
		</v-main>
		<!-- dettaglio dalla lente delle pagine CMS (non hanno ancora una route propria): le altre aprono la loro pagina -->
		<v-dialog :eager="true" :retain-focus="false" :value="getDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
			<component :is="form" v-if="form" :key="componentKey" @event="chiudiDettaglio" @message="messaggioDettaglio" />
		</v-dialog>
	</div>
</template>

<script>
import { openWithReturn } from '@/components/core/pageReturn';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import _ from 'lodash';
import PageRow from '@/components/analytics/PageRow.vue';
import { listRouteState } from '@/components/core/list/listRouteState';

import { meseEsteso, nomeLingua } from '@/components/analytics/format';

// form della pagina CMS: e' l'unico dettaglio che non ha ancora una pagina sua
const PAGE_FORM = () => import('@/components/ecommerce/page/Edit.vue');

const LIMIT = 25;
const POLLING_MS = 5000;

// Analytics = visite mensili di ogni pagina del sito pubblico, una lingua per tab
export default {
	name: 'Analytics',
	components: { PageRow },
	// lingua, ordinamento, ricerca e pagina stanno nell'URL: refresh e ritorno dal dettaglio ripartono da li'
	mixins: [listRouteState({ filters: { lang: 'string' }, fetch: 'ricarica' })],
	data() {
		return {
			data: null,
			status: null,
			loading: false,
			error: '',
			nonConfigurato: false,
			form: null,
			componentKey: 0,
			tab: 0,
			searchfield: '',
			// lang vuota = la lingua predefinita scelta dal backend; ordinate per le visite dell'ultimo mese
			settings: this.listSettingsFromRoute({
				search: '',
				filters: { lang: '' },
				options: { sortBy: ['views'], sortDesc: [true], page: 1, itemsPerPage: LIMIT },
			}),
			timerRicerca: null,
			timerStato: null,
		};
	},
	computed: {
		...mapGetters({ getDialog: 'settings/getDialog' }),
		sort() {
			return this.settings.options.sortDesc && this.settings.options.sortDesc[0] === false ? 'asc' : 'desc';
		},
		linguaAttiva() {
			return this.data ? this.data.languages.find(l => l.lang === this.data.lang) : null;
		},
		ultimoMese() {
			return this.data && this.data.months.length ? meseEsteso(this.data.months[this.data.months.length - 1]) : '';
		},
		pagine() {
			return this.data ? Math.ceil(this.data.total / LIMIT) : 0;
		},
	},
	watch: {
		// cambio di lingua dall'utente: il mixin torna a pagina 1, aggiorna l'URL e ricarica
		tab(index) {
			const l = this.data && this.data.languages[index];
			if (!l || l.lang === this.data.lang) return;
			this.settings.filters.lang = l.lang;
		},
	},
	created() {
		this.listLoad();
	},
	beforeDestroy() {
		clearTimeout(this.timerRicerca);
		clearTimeout(this.timerStato);
	},
	methods: {
		...mapActions({
			fetchPages: 'stats/asyncFetchPages',
			fetchStatus: 'stats/asyncFetchPagesStatus',
			setSnackbar: 'settings/updateSnackbar',
		}),
		...mapMutations({
			setDialog: 'settings/setDialog',
			setClearError: 'settings/setClearError',
			setMessages: 'settings/setMessages',
			setPage: 'pages/setPage',
		}),
		// lente: apre il dettaglio dello slug della pagina
		apriDettaglio(row) {
			// prodotti, categorie e marche hanno la loro pagina: ci si va e si torna qui
			const pagina =
				row.section === 'item'
					? `/items/${row.reference}`
					: row.section === 'category' && row.numericId != null
					? `/categories/${row.numericId}`
					: row.section === 'brand'
					? `/brands/${row.reference}`
					: null;
			if (pagina) {
				openWithReturn(this, { path: pagina }, { label: 'Torna ad Analytics' });
				return;
			}
			if (row.section !== 'page' || row.numericId == null) {
				this.setSnackbar({ color: 'error', text: 'Elemento non trovato: potrebbe essere stato cancellato', snackbar: true, timeout: 5000 });
				return;
			}
			this.setPage({ _id: row.reference, id: row.numericId });
			this.componentKey += 1;
			this.setClearError({});
			this.setMessages();
			this.form = PAGE_FORM;
			this.setDialog(true);
		},
		chiudiDettaglio() {
			this.form = null;
			this.setDialog(false);
		},
		messaggioDettaglio(text, value, options) {
			this.setSnackbar({ color: options && options.severity === 'error' ? 'error' : 'success', text, snackbar: true, mode: 'multi-line', timeout: 5000 });
			this.form = null;
		},
		nomeLingua,
		// il caricamento (e il ritorno a pagina 1) lo fa il mixin quando cambia settings.search
		cercaDopo() {
			clearTimeout(this.timerRicerca);
			this.timerRicerca = setTimeout(() => {
				this.settings.search = _.size(this.searchfield) >= 3 ? this.searchfield : '';
			}, 400);
		},
		// Piu' viste / Meno viste: si riparte dalla prima pagina
		cambiaOrdine(value) {
			this.settings.options = { ...this.settings.options, sortDesc: [value !== 'asc'], page: 1 };
		},
		async ricarica() {
			this.loading = true;
			this.error = '';
			try {
				const { filters, options, search } = this.settings;
				const data = await this.fetchPages({ lang: filters.lang || undefined, sort: this.sort, page: options.page, limit: LIMIT, search: search || undefined });
				this.data = data;
				this.tab = Math.max(
					0,
					data.languages.findIndex(l => l.lang === data.lang),
				);
				this.status = data.status;
				this.nonConfigurato = false;
				if (data.status && data.status.syncing) this.attendiScansione();
			} catch (e) {
				const res = e.response && e.response.data;
				if (res && res.message === 'analytics_not_configured') this.nonConfigurato = true;
				else this.error = (res && res.error && res.error[0] && res.error[0].msg) || 'Impossibile caricare le visite delle pagine';
			} finally {
				this.loading = false;
			}
		},
		// prima scansione in corso: controlla lo stato finche' non finisce, poi ricarica le pagine
		attendiScansione() {
			clearTimeout(this.timerStato);
			this.timerStato = setTimeout(async () => {
				try {
					const prima = this.status ? this.status.ready : 0;
					this.status = await this.fetchStatus();
					if (!this.status.syncing || this.status.ready !== prima) await this.ricarica();
					else this.attendiScansione();
				} catch (e) {
					this.attendiScansione();
				}
			}, POLLING_MS);
		},
	},
};
</script>
